import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Nav from "@/components/landing/Nav";
import Footer from "@/components/landing/Footer";
import FinalCTA from "@/components/landing/FinalCTA";
import MedCard from "@/components/shared/MedCard";
import { ARTICLES_BY_SLUG, ARTICLES, ARTICLE_CATEGORIES } from "@/data/articles";
import SEO, { articleSchema, breadcrumbSchema } from "@/components/shared/SEO";

export default function ArticlePage() {
  const { slug } = useParams();
  const article = ARTICLES_BY_SLUG[slug];
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!article) return (
    <main className="bg-[var(--leen-bg)] min-h-screen text-white">
      <SEO title="Article Not Found" path={`/learn/${slug}`} noindex />
      <Nav />
      <div className="pt-40 text-center px-6">
        <h1 className="font-display text-5xl">Article not found</h1>
        <Link to="/learn" className="btn-pink mt-8 inline-flex">Resource Center →</Link>
      </div>
      <Footer />
    </main>
  );

  const related = (article.related || []).map((r) => ARTICLES_BY_SLUG[r]).filter(Boolean);
  const meds = (article.related || []).filter((r) => !ARTICLES_BY_SLUG[r]);
  const path = `/learn/${slug}`;

  return (
    <main className="bg-[var(--leen-bg)] text-white overflow-x-hidden min-h-screen">
      <SEO
        title={article.title}
        path={path}
        description={article.summary}
        type="article"
        schema={[
          articleSchema(article, path),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Resource Center", path: "/learn" },
            { name: article.title, path },
          ]),
        ]}
      />
      <Nav />
      <article className="pt-28 md:pt-36 pb-16">
        <div className="px-5 md:px-8 lg:px-12 max-w-[900px] mx-auto">
          <Link to="/learn" className="eyebrow text-[var(--leen-pink)] mb-5 inline-block">← Resource Center</Link>
          <p className="eyebrow text-[var(--leen-ink-soft)] mb-3">{article.category} · {article.readTime} min read</p>
          <h1 className="font-display text-[36px] sm:text-[52px] md:text-[68px] lg:text-[80px] leading-[0.95] tracking-tight mb-6">
            {article.title}
          </h1>
          <p className="font-body italic text-[18px] md:text-[20px] text-[var(--leen-ink-soft)] leading-relaxed mb-10">
            {article.summary}
          </p>
          <div className="space-y-8 text-[16px] md:text-[17px] leading-[1.75] text-white/90">
            {article.sections.map((s, i) => (
              <section key={i} data-testid={`article-section-${i}`}>
                <h2 className="font-display text-[22px] md:text-[28px] tracking-tight mb-3 text-white">{s.h}</h2>
                <p className="text-[var(--leen-ink-soft)]">{s.body}</p>
              </section>
            ))}
          </div>

          <div className="mt-14 p-7 md:p-9 rounded-xl border border-[var(--leen-pink)]/40 bg-[var(--leen-card)]">
            <p className="font-script italic text-[var(--leen-pink)]" style={{ fontSize: "clamp(36px, 4vw, 52px)", lineHeight: 1 }}>Ready?</p>
            <h3 className="font-display text-[24px] md:text-[32px] mt-1 mb-4">START YOUR ASSESSMENT</h3>
            <p className="text-[14px] text-[var(--leen-ink-soft)] mb-6 italic">Four minutes. Clinician review within 24 hours. Plan shipped to your door.</p>
            <a href="#start-assessment" className="btn-pink" data-testid={`article-${slug}-cta`}>Start Now <span aria-hidden>→</span></a>
          </div>

          {meds.length > 0 && (
            <div className="mt-16">
              <p className="eyebrow text-[var(--leen-pink)] mb-5">Related medications</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {meds.map((s) => <MedCard key={s} slug={s} />)}
              </div>
            </div>
          )}

          {related.length > 0 && (
            <div className="mt-16">
              <p className="eyebrow text-[var(--leen-pink)] mb-5">Continue reading</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {related.map((r) => (
                  <Link key={r.slug} to={`/learn/${r.slug}`} className="block p-5 rounded-xl border border-[var(--leen-line)] hover:border-[var(--leen-pink)]" data-testid={`related-article-${r.slug}`}>
                    <p className="eyebrow text-[var(--leen-pink)] mb-2">{r.category}</p>
                    <h4 className="font-display text-[18px] md:text-[20px] leading-tight">{r.title}</h4>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
      <FinalCTA />
      <Footer />
    </main>
  );
}

export function ResourceCenter() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <main className="bg-[var(--leen-bg)] text-white overflow-x-hidden min-h-screen">
      <SEO
        title="Resource Center · Clinician-Reviewed Articles"
        path="/learn"
        description={`${ARTICLES.length} clinician-reviewed articles on weight loss, men's and women's health, peptides, hair, skin, and longevity from BestRx.`}
      />
      <Nav />
      <section className="relative pt-28 md:pt-36 pb-16 md:pb-20 overflow-hidden">
        <div className="absolute -top-20 -left-40 w-[480px] h-[480px] rounded-full blur-3xl bg-[var(--leen-pink)]/20" />
        <div className="absolute -bottom-32 right-0 w-[520px] h-[520px] rounded-full blur-3xl bg-[var(--leen-blue)]/20" />
        <div className="relative px-5 md:px-8 lg:px-12 max-w-[1500px] mx-auto">
          <p className="font-script italic text-[var(--leen-pink)]" style={{ fontSize: "clamp(48px, 6vw, 96px)", lineHeight: 1 }}>The</p>
          <h1 className="font-display text-[52px] sm:text-[88px] md:text-[120px] lg:text-[160px] leading-[0.88] tracking-tight -mt-2">
            RESOURCE <span className="text-[var(--leen-pink)]">CENTER</span>
          </h1>
          <p className="mt-7 font-body italic text-[18px] md:text-[22px] text-[var(--leen-ink-soft)] max-w-2xl">
            {ARTICLES.length} clinician-reviewed articles on weight loss, men's & women's health, peptides, hair, skin, and longevity.
          </p>
        </div>
      </section>

      {ARTICLE_CATEGORIES.map((cat) => {
        const list = ARTICLES.filter((a) => a.category === cat);
        return (
          <section key={cat} className="border-t border-[var(--leen-line)] py-14 md:py-20">
            <div className="px-5 md:px-8 lg:px-12 max-w-[1500px] mx-auto">
              <h2 className="font-display text-[28px] md:text-[40px] mb-7">
                <span className="text-[var(--leen-pink)]">{cat.toUpperCase()}</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {list.map((a) => (
                  <Link key={a.slug} to={`/learn/${a.slug}`} className="block p-5 md:p-6 rounded-xl border border-[var(--leen-line)] hover:border-[var(--leen-pink)] bg-[var(--leen-card)]" data-testid={`article-card-${a.slug}`}>
                    <p className="eyebrow text-[var(--leen-pink)] mb-3">{a.category} · {a.readTime} MIN</p>
                    <h3 className="font-display text-[18px] md:text-[20px] leading-tight mb-2">{a.title}</h3>
                    <p className="text-[13px] text-[var(--leen-ink-soft)] line-clamp-2 italic">{a.summary}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        );
      })}
      <FinalCTA />
      <Footer />
    </main>
  );
}
